import supabase from "../services/supabaseClient";

export const ROOM_FILES_BUCKET = "room-files";

// Matches the room-files bucket's server-side file_size_limit (see
// supabase/setup/room-files-bucket.sql) — checked here too so a too-large file
// fails fast with a clear message instead of after a full upload attempt.
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export function buildRoomFilePath(userId, roomId, fileName) {
  if (!userId || !roomId) {
    throw new Error("buildRoomFilePath requires userId and roomId");
  }
  const safeName = String(fileName || "file").replace(/[^a-zA-Z0-9._-]/g, "_");
  return `${userId}/${roomId}/${Date.now()}-${safeName}`;
}

export async function uploadRoomFile(userId, roomId, file) {
  if (!file) {
    throw new Error("uploadRoomFile requires a file");
  }
  if (file.size != null && file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`"${file.name}" is larger than 5MB. Choose a smaller file.`);
  }
  const storagePath = buildRoomFilePath(userId, roomId, file.name);

  const { error } = await supabase.storage.from(ROOM_FILES_BUCKET).upload(storagePath, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;

  return {
    storagePath,
    fileName: file.name || "file",
    mimeType: file.type || null,
    fileSize: file.size ?? null,
  };
}

// Pass a `download` filename to get a URL that responds with Content-Disposition:
// attachment. The browser ignores an <a download> attribute on cross-origin URLs,
// so the header is the only way to save the file under its original name.
export async function getRoomFileUrl(storagePath, expiresInSeconds = 3600, { download } = {}) {
  if (!storagePath) return null;
  const { data, error } = await supabase.storage
    .from(ROOM_FILES_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds, download ? { download } : undefined);
  if (error) throw error;
  return data?.signedUrl || null;
}

export async function deleteRoomFile(storagePath) {
  if (!storagePath) return;
  const { error } = await supabase.storage.from(ROOM_FILES_BUCKET).remove([storagePath]);
  if (error) throw error;
}

export const WHITEBOARD_IMAGE_URL_TTL_SECONDS = 24 * 60 * 60;

async function uploadSnapshot(userId, roomId, dataUrl, fileName) {
  if (!dataUrl) throw new Error(`${fileName} upload requires an image`);
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], fileName, { type: "image/jpeg" });
  const { storagePath } = await uploadRoomFile(userId, roomId, file);
  return storagePath;
}

export const uploadWhiteboardSnapshot = (userId, roomId, dataUrl) =>
  uploadSnapshot(userId, roomId, dataUrl, "whiteboard.jpg");

export const uploadBulletinSnapshot = (userId, roomId, dataUrl) =>
  uploadSnapshot(userId, roomId, dataUrl, "bulletin-board.jpg");

export async function getRoomFileUrls(storagePaths, expiresInSeconds = 3600) {
  const paths = [...new Set((storagePaths || []).filter(Boolean))];
  if (!paths.length) return new Map();
  const { data, error } = await supabase.storage
    .from(ROOM_FILES_BUCKET)
    .createSignedUrls(paths, expiresInSeconds);
  if (error) throw error;
  return new Map((data || [])
    .filter((entry) => entry?.signedUrl && !entry.error)
    .map((entry) => [entry.path, entry.signedUrl]));
}
